import { memo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";

import type { QuestionnaireQuestionFormValues } from "../types";

export interface SortableQuestionListItemProps {
  id: string;
  question: QuestionnaireQuestionFormValues;
  isActive: boolean;
  canDelete: boolean;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
  onDuplicate: (e: React.MouseEvent) => void;
}

const SortableQuestionListItem = memo(
  ({
    id,
    question,
    isActive,
    canDelete,
    onClick,
    onDelete,
    onDuplicate,
  }: SortableQuestionListItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const style = {
      transform: transform
        ? `translate3d(0px, ${transform.y}px, 0)`
        : undefined,
      transition,
    };

    const labelText = question.label || "Nova pergunta";
    const displayLabel =
      labelText.length > 30 ? `${labelText.slice(0, 30)}…` : labelText;

    const typeLabel =
      question.type === "shortText"
        ? "Resposta curta"
        : question.type === "longText"
          ? "Resposta longa"
          : question.type === "yesNo"
            ? "Sim / Não"
            : "Múltipla escolha";

    return (
      <ListItem
        ref={setNodeRef}
        disablePadding
        style={style}
        secondaryAction={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {question.isRequired && (
              <Chip
                size="small"
                label="Obrigatório"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            )}
            <IconButton
              size="small"
              onClick={onDuplicate}
              aria-label="Duplicar pergunta"
            >
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={!canDelete}
              aria-label={
                canDelete
                  ? "Deletar pergunta"
                  : "Deletar pergunta (desabilitado: é necessário manter pelo menos uma pergunta)"
              }
              title={
                !canDelete
                  ? "É necessário manter pelo menos uma pergunta"
                  : undefined
              }
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
              {...listeners}
              {...attributes}
            >
              <DragIndicatorIcon
                sx={{ color: "text.disabled", fontSize: 20 }}
              />
            </Box>
          </Box>
        }
      >
        <ListItemButton selected={isActive} onClick={onClick}>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: isActive ? 600 : 400 }}
              >
                {displayLabel}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" noWrap>
                {typeLabel}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  },
);

SortableQuestionListItem.displayName = "SortableQuestionListItem";

export default SortableQuestionListItem;
